import { useMemo, useState } from 'react'
import { Button, Grid, GridRow, Header } from 'semantic-ui-react'
import { Alert } from '@carsys/ui'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ReduxDateTimePicker as DateTimePicker } from '@cbgms/components/form/DateTimePickerBasic'
import { formatters } from '@cbgms/base/utils/datetime'
import { postNewHaynesLicense } from 'app-support/licenses/state/actions'
import { formValueSelector } from 'redux-form'
import { IRootReducerState } from 'state/reducers'
import { IHaynesProductType } from '@cbgms/api'
import Dropdown from '@cbgms/components/form/Dropdown'

interface props {
  distributorID?: string
  customerID: string
  form: string
  productTypes: IHaynesProductType[]
  onLicenseCreate: () => void
}

const licensePropProductType = 'haynesLicense.ProductType'
const licensePropStartDate = 'haynesLicense.StartDate'
const licensePropEndDate = 'haynesLicense.EndDate'

export const HaynesProNewLicense = ({ distributorID, customerID, form, onLicenseCreate, productTypes }: props) => {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const dispatch = useDispatch()
  const selectedProductType = useSelector((state: IRootReducerState) => formValueSelector(form)(state, licensePropProductType))
  const selectedStartDate = useSelector((state: IRootReducerState) => formValueSelector(form)(state, licensePropStartDate))
  const selectedEndDate = useSelector((state: IRootReducerState) => formValueSelector(form)(state, licensePropEndDate))
  const productTypeOptions = useMemo(() => {
    return productTypes.map(t => ({ text: t.Name, value: t.ID }))
  }, [productTypes])

  const createLicense = async () => {
    if (!distributorID) {
      setMessage(t('haynespro.distributor_id_not_configured'))
      return
    }
    if (!selectedStartDate || !selectedEndDate) {
      setMessage(t('haynespro.provide_a_date_range'))
      return
    }
    try {
      const lic = {
        DistributorID: Number(distributorID),
        CustomerID: customerID,
        StartDate: selectedStartDate,
        EndDate: selectedEndDate,
        ProductTypeID: Number(selectedProductType)
      }

      await dispatch(postNewHaynesLicense(lic))
      setMessage('')
      onLicenseCreate()
    } catch (e: any) {
      setMessage(e.toString())
    }
  }

  return (
    <>
      <Header as='h3'>{t('Company.Licenses')}</Header>
      <Grid>
        <GridRow>
          <Alert severity='info' sx={{ mb: 1 }}>
            {t('haynespro.license_not_configured')}
          </Alert>
          <Dropdown
            label={t('haynespro.select_product_type_dropdown')}
            data-testid='haynes-dropdown-select-product_type'
            name={licensePropProductType}
            required
            fluid
            search
            selection
            className='full-width'
            options={productTypeOptions}
          />
        </GridRow>
        <GridRow>
          <DateTimePicker
            formatters={formatters.date}
            label={t('companyLicense.validfrom')}
            name={licensePropStartDate}
            timeFormat={false}
            viewMode='years'
            data-testid='date-valid-from'
          />
        </GridRow>
        <GridRow>
          <DateTimePicker
            formatters={formatters.date}
            label={t('companyLicense.validtill')}
            name={licensePropEndDate}
            timeFormat={false}
            viewMode='years'
            data-testid='date-valid-till'
          />
        </GridRow>
        <GridRow>
          <Button primary onClick={createLicense}>
            {t('haynespro.create_new_license')}
          </Button>
        </GridRow>
        {message && <Alert severity='error'>{message}</Alert>}
      </Grid>
    </>
  )
}

export default HaynesProNewLicense
